import { useMemo } from 'react'
import { Chip } from '@heroui/react'

import { useGifs } from '../../gifs/hooks'
import { gifDisplayTitle } from '../../gifs/types'
import type { Gif } from '../../gifs/types'

import { GifThumbnail } from './GifThumbnail'

interface RetrievalPoolEntry {
  gifId: string
  score: number
}

interface RetrievalPoolPreviewProps {
  pool: RetrievalPoolEntry[]
  /** GIF ids that survived rerank into the final result, for visual contrast. */
  returnedGifIds?: string[]
  className?: string
}

/**
 * Read-only view of the stage-A retrieval candidate pool for a single
 * recommendation log. Renders rank, cosine score, and the gif (thumbnail +
 * title, id fallback for gifs no longer in the library) so an admin can tell
 * whether a wanted gif missed at retrieval or at rerank. Reuses the same
 * client gif cache as the rest of the admin log viewer.
 */
export function RetrievalPoolPreview({
  pool,
  returnedGifIds = [],
  className,
}: RetrievalPoolPreviewProps) {
  const { gifs, loading } = useGifs()

  const gifsById = useMemo(() => {
    const map = new Map<string, Gif>()
    gifs.forEach((gif) => map.set(gif.id, gif))
    return map
  }, [gifs])

  const returnedSet = useMemo(() => new Set(returnedGifIds), [returnedGifIds])

  if (pool.length === 0) {
    return (
      <div className={`text-center text-default-500 py-4 ${className ?? ''}`}>
        Retrieval pool is empty
      </div>
    )
  }

  return (
    <div className={className}>
      {loading && (
        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-300">
          Loading GIF collection...
        </div>
      )}

      <div className="space-y-1">
        {pool.map((entry, index) => {
          const gif = gifsById.get(entry.gifId) ?? null
          const isReturned = returnedSet.has(entry.gifId)

          return (
            <div
              key={entry.gifId}
              className={`flex items-center gap-3 p-2 rounded-lg border ${
                isReturned
                  ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                  : 'bg-slate-50 border-slate-200 dark:bg-slate-800 dark:border-slate-700'
              }`}
            >
              <span className="w-8 shrink-0 text-center font-mono text-xs text-slate-500 dark:text-slate-400">
                #{index + 1}
              </span>

              <div className="w-12 shrink-0">
                {gif ? (
                  <GifThumbnail gif={gif} />
                ) : (
                  <div className="aspect-square bg-slate-200 dark:bg-slate-700 rounded overflow-hidden flex items-center justify-center">
                    <div className="text-slate-500 text-[10px] font-mono text-center px-1 leading-tight">
                      {entry.gifId.slice(-8)}
                    </div>
                  </div>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="truncate text-xs text-slate-800 dark:text-slate-200">
                  {gif ? (
                    gifDisplayTitle(gif)
                  ) : (
                    <span className="italic text-slate-500">
                      Not in library
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-slate-400">
                  {entry.gifId}
                </div>
              </div>

              {isReturned && (
                <Chip size="sm" variant="flat" color="success">
                  returned
                </Chip>
              )}

              <span className="w-16 shrink-0 text-right font-mono text-xs text-slate-600 dark:text-slate-300">
                {entry.score.toFixed(3)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
